import React from 'react';
import { FieldError, UseFormRegisterReturn } from 'react-hook-form';
import { defineMessages, FormattedMessage } from 'react-intl';

import * as yup from 'yup';

import { errorUtils } from '../../utils/errors/errors';
import {
  Input,
  InputState,
  InputType,
  BaseFormInputProps,
} from '../Input/Input';

export enum FormInputEmailLabelName {
  billingEmailBasketGuestOrderForm = 'billingEmailBasketGuestOrderForm',
  billingEmailLabel = 'billingEmailLabel',
  emailLabel = 'emailLabel',
  emailRequiredLabel = 'emailRequiredLabel',
  invoiceEmailLabel = 'invoiceEmailLabel',
  invoiceEmailRequiredLabel = 'invoiceEmailRequiredLabel',
}

const messages = defineMessages({
  emailLabel: {
    id: 'src.components.FormInputEmail.emailLabel',
    defaultMessage: 'Email address',
  },
  emailRequiredLabel: {
    id: 'src.components.FormInputEmail.emailRequiredLabel',
    defaultMessage: 'Email address *',
  },
  invoiceEmailLabel: {
    id: 'src.components.FormInputEmail.invoiceEmailLabel',
    defaultMessage: 'Invoice email address',
  },
  invoiceEmailRequiredLabel: {
    id: 'src.components.FormInputEmail.invoiceEmailRequiredLabel',
    defaultMessage: 'Invoice email address *',
  },
  billingEmailLabel: {
    id: 'src.components.FormInputEmail.billingEmailLabel',
    defaultMessage: 'Invoice email address',
  },
  emailEmptyError: {
    id: 'src.components.FormInputEmail.emailEmptyError',
    defaultMessage: 'Email address is a required field',
  },
  emailInvalidError: {
    id: 'src.components.FormInputEmail.emailInvalidError',
    defaultMessage: 'Please enter valid email',
  },
  userAlreadyExists: {
    id: 'src.containers.FormInputEmail.userAlreadyExists',
    defaultMessage: 'User already exists',
  },
  billingEmailBasketGuestOrderForm: {
    id: 'src.components.FormInputEmail.billingEmailBasketGuestOrderForm',
    defaultMessage: 'Email address',
  },
});

export const FORM_INPUT_EMAIL = 'email';

export const emailValidationSchema = yup.string().email('emailInvalidError');

export const emailRequiredValidationSchema =
  yup.string().required('emailEmptyError').email('emailInvalidError');

export type FormInputEmailErrors = 'emailEmptyError' | 'emailInvalidError';

export type FormFieldsEmail = {
  [FORM_INPUT_EMAIL]: string;
};

export type FormFieldsInvoiceEmail = {
  invoiceEmail: string;
};

export type FormFieldsBillingEmail = {
  billingEmail: string;
};

export const formFieldsEmailDefaultValue: FormFieldsEmail = {
  [FORM_INPUT_EMAIL]: '',
};

export const formFieldsInvoiceEmailDefaultValue: FormFieldsInvoiceEmail = {
  invoiceEmail: '',
};

export const formFieldsBillingEmailDefaultValue: FormFieldsBillingEmail = {
  billingEmail: '',
};

export type Props = UseFormRegisterReturn & BaseFormInputProps & {
  error?: FieldError | string;
  isDisabled?: boolean;
  labelName?: FormInputEmailLabelName;
};

export const FormInputEmail = React.memo(React.forwardRef((
  props: Props,
  ref: React.ForwardedRef<HTMLInputElement | HTMLTextAreaElement>,
) => {
  const {
    error,
    labelName = FormInputEmailLabelName.emailRequiredLabel,
    isDisabled = false,
    ...rest
  } = props;

  return (
    <Input
      inputType={InputType.Input}
      state={
        isDisabled && InputState.Disabled
        || error && InputState.Error
        || undefined
      }
      label={<FormattedMessage {...messages[labelName]} />}
      errorMessage={errorUtils.convertErrorMessage({
        messages,
        error,
      })}
      type='text'
      {...rest}
      ref={ref}
    />
  );
}));

FormInputEmail.displayName = 'FormInputEmail';
